import React from 'react';
import { X, Copy, Share, Zap, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { cleanTransactionDescription } from '../utils/transactionDisplay';
import { APP_CONFIG } from '../config/app-config';
import { getToken } from '../utils/authToken';
import { buildVoucherUssdDialString, resolveVoucherRedemptionGuide } from './vouchers/voucherRedemptionGuides';
import { VoucherRedemptionInstructions } from './vouchers/VoucherRedemptionInstructions';
import { formatPinForDisplay, pinToClipboardValue } from '../utils/formatPin';

interface Transaction {
  id: string;
  transactionId?: string;
  reference?: string;
  amount: number;
  type: string;
  description: string;
  timestamp: string;
  status: string;
  metadata?: {
    electricityToken?: string;
    beneficiaryName?: string;
    beneficiaryMeter?: string;
    meterType?: string;
    purchasedAt?: string;
    vasType?: string;
    [key: string]: any;
  };
}

interface TransactionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

export function TransactionDetailModal({ isOpen, onClose, transaction }: TransactionDetailModalProps) {
  const [showCashoutCredential, setShowCashoutCredential] = React.useState(false);
  const [cashoutCopied, setCashoutCopied] = React.useState(false);
  const [credentialStepUp, setCredentialStepUp] = React.useState('');
  const [credentialRevealError, setCredentialRevealError] = React.useState<string | null>(null);
  const [credentialRevealLoading, setCredentialRevealLoading] = React.useState(false);
  const [revealedCashoutCredentials, setRevealedCashoutCredentials] = React.useState<any | null>(null);
  const [showVoucherPin, setShowVoucherPin] = React.useState(false);
  const [voucherCopied, setVoucherCopied] = React.useState(false);
  const [voucherCopiedLabel, setVoucherCopiedLabel] = React.useState<string | null>(null);
  const [voucherStepUp, setVoucherStepUp] = React.useState('');
  const [voucherRevealError, setVoucherRevealError] = React.useState<string | null>(null);
  const [voucherRevealLoading, setVoucherRevealLoading] = React.useState(false);
  const [revealedVoucher, setRevealedVoucher] = React.useState<any | null>(null);

  React.useEffect(() => {
    setShowCashoutCredential(false);
    setCashoutCopied(false);
    setCredentialStepUp('');
    setCredentialRevealError(null);
    setCredentialRevealLoading(false);
    setRevealedCashoutCredentials(null);
    setShowVoucherPin(false);
    setVoucherCopied(false);
    setVoucherCopiedLabel(null);
    setVoucherStepUp('');
    setVoucherRevealError(null);
    setVoucherRevealLoading(false);
    setRevealedVoucher(null);
  }, [isOpen, transaction?.id]);

  if (!isOpen || !transaction) return null;
  const displayDescription = cleanTransactionDescription(transaction);
  const cashoutCredentialBundle = revealedCashoutCredentials?.cashoutCredentials || transaction.metadata?.cashoutCredentials;
  const cashoutCredentialParts = Array.isArray(cashoutCredentialBundle?.parts)
    ? cashoutCredentialBundle.parts
    : (transaction.metadata?.cashoutCredential?.maskedCode ? [transaction.metadata.cashoutCredential] : []);
  const hasCashoutCredential = cashoutCredentialParts.length > 0;
  const voucherMeta = revealedVoucher?.voucher || transaction.metadata?.voucher;
  const voucherCredentialBundle = revealedVoucher?.voucherCredentials || transaction.metadata?.voucherCredentials;
  const voucherCredentialParts = Array.isArray(voucherCredentialBundle?.parts)
    ? voucherCredentialBundle.parts
    : (voucherMeta?.maskedCode ? [voucherMeta] : []);
  const hasVoucherCredential = voucherCredentialParts.length > 0;
  const voucherRevealAvailable = voucherCredentialParts.some((part: any) => part?.revealAvailable !== false)
    || voucherMeta?.revealAvailable !== false;
  const voucherSmsNotice = typeof transaction.metadata?.voucherSmsNotice === 'string'
    ? transaction.metadata.voucherSmsNotice
    : null;

  const isEeziAirtimeToken = transaction.metadata?.isEeziAirtimeToken === true ||
                             transaction.metadata?.operationType === 'eezi_airtime_token' ||
                             transaction.description?.toLowerCase().includes('eeziairtime');

  const isEeziPowerToken = transaction.metadata?.isEeziPowerToken === true ||
                           transaction.metadata?.operationType === 'eezi_power_token' ||
                           transaction.description?.toLowerCase().includes('eezipower');

  const voucherRedemptionGuide = hasVoucherCredential
    ? resolveVoucherRedemptionGuide({
        catalogKey: transaction.metadata?.catalogKey,
        description: transaction.description,
        productName: transaction.metadata?.productName,
        name: displayDescription,
        isEeziAirtimeToken,
        isEeziPowerToken: false,
      })
    : null;
  const eeziPowerRedemptionGuide = isEeziPowerToken
    ? resolveVoucherRedemptionGuide({
        description: transaction.description,
        productName: transaction.metadata?.productName,
        name: displayDescription,
        operationType: transaction.metadata?.operationType,
        isEeziPowerToken: true,
        isEeziAirtimeToken: false,
      })
    : null;
  const eeziAirtimeRedemptionGuide = isEeziAirtimeToken
    ? resolveVoucherRedemptionGuide({
        description: transaction.description,
        productName: transaction.metadata?.productName,
        name: displayDescription,
        operationType: transaction.metadata?.operationType,
        isEeziAirtimeToken: true,
        isEeziPowerToken: false,
      })
    : null;

  const isElectricity = transaction.metadata?.vasType === 'electricity' || 
                        transaction.description?.toLowerCase().includes('electricity');
  
  const isCashOut = transaction.metadata?.vasType === 'cash_out' ||
                    transaction.description?.toLowerCase().includes('eezi cash') ||
                    transaction.description?.toLowerCase().includes('cash out');

  const isGlobalServices = transaction.metadata?.vasType === 'global_services';
  const isEeziToken = isEeziAirtimeToken || isEeziPowerToken;
  const eeziTokenLabel = isEeziPowerToken ? 'eeziPower' : 'eeziAirtime';

  const handleCopyToken = () => {
    let tokenToCopy: string | undefined;
    if (isEeziAirtimeToken && transaction.metadata?.pin && eeziAirtimeRedemptionGuide) {
      tokenToCopy = buildVoucherUssdDialString(eeziAirtimeRedemptionGuide, transaction.metadata.pin) || undefined;
    } else if (isEeziToken && transaction.metadata?.pin) {
      tokenToCopy = transaction.metadata.pin;
    } else {
      tokenToCopy = transaction.metadata?.electricityToken || transaction.metadata?.pin;
    }
    if (tokenToCopy) {
      navigator.clipboard.writeText(pinToClipboardValue(tokenToCopy));
      const button = document.querySelector('[data-copy-token]') as HTMLElement;
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = originalText || 'Copy PIN';
        }, 2000);
      }
    }
  };

  const handleRevealCashoutCredentials = async () => {
    if (!transaction?.transactionId || !credentialStepUp.trim()) return;
    setCredentialRevealLoading(true);
    setCredentialRevealError(null);
    try {
      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/wallets/transactions/${encodeURIComponent(transaction.transactionId)}/reveal-cashout-credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential: credentialStepUp }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'We could not reveal these credentials.');
      }
      setRevealedCashoutCredentials(payload.data || null);
      setShowCashoutCredential(true);
      setCredentialStepUp('');
      window.setTimeout(() => {
        setShowCashoutCredential(false);
        setRevealedCashoutCredentials(null);
      }, Math.max(10, Number(payload.data?.revealExpiresInSeconds || 60)) * 1000);
    } catch (error: any) {
      setCredentialRevealError(error?.message || 'We could not reveal these credentials.');
    } finally {
      setCredentialRevealLoading(false);
    }
  };

  const handleCopyCashoutCredential = async (value: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(pinToClipboardValue(value));
    setCashoutCopied(true);
    setTimeout(() => setCashoutCopied(false), 2000);
  };

  const handleRevealVoucherCredentials = async () => {
    if (!transaction?.transactionId || !voucherStepUp.trim()) return;
    setVoucherRevealLoading(true);
    setVoucherRevealError(null);
    try {
      const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/wallets/transactions/${encodeURIComponent(transaction.transactionId)}/reveal-voucher-credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential: voucherStepUp }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'We could not reveal this voucher PIN.');
      }
      setRevealedVoucher(payload.data || null);
      setShowVoucherPin(true);
      setVoucherStepUp('');
      window.setTimeout(() => {
        setShowVoucherPin(false);
        setRevealedVoucher(null);
      }, Math.max(10, Number(payload.data?.revealExpiresInSeconds || 60)) * 1000);
    } catch (error: any) {
      setVoucherRevealError(error?.message || 'We could not reveal this voucher PIN.');
    } finally {
      setVoucherRevealLoading(false);
    }
  };

  const handleCopyVoucherPin = async (value: string, label?: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(pinToClipboardValue(value));
    setVoucherCopied(true);
    setVoucherCopiedLabel(label || null);
    setTimeout(() => {
      setVoucherCopied(false);
      setVoucherCopiedLabel(null);
    }, 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTransactionType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'money_in': 'Money Received',
      'money_out': 'Money Sent',
      'send': 'Money Sent',
      'receive': 'Money Received',
      'payment': 'Payment',
      'deposit': 'Deposit',
      'withdrawal': 'Withdrawal',
      'fee': 'Fee',
      'refund': 'Refund',
      'sent': 'Money Sent',
      'received': 'Money Received',
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <Card style={{
        position: 'relative',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        maxWidth: '420px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <CardHeader style={{
          borderBottom: '1px solid #e2e8f0',
          padding: '1rem',
          backgroundColor: '#f9fafb'
        }}>
          <div className="flex items-center justify-between pr-10">
            <div className="flex items-center gap-3">
              {isElectricity && (
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#86BE41',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Zap style={{ width: '20px', height: '20px', color: '#ffffff' }} />
                </div>
              )}
              <div>
                <h2 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0
                }}>
                  {isElectricity ? 'Electricity Purchase' : 'Transaction Details'}
                </h2>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  {formatDate(transaction.timestamp)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="universal-close-btn"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </CardHeader>

        <CardContent style={{ padding: '1.5rem' }}>
          {/* Electricity Token (if available) */}
          {isElectricity && transaction.metadata?.electricityToken && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '12px',
                border: '2px dashed #d1d5db'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Zap style={{
                    width: '32px',
                    height: '32px',
                    color: '#f59e0b',
                    margin: '0 auto 8px'
                  }} />
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your Electricity Token
                  </p>
                  <p style={{
                    fontFamily: 'Monaco, Courier, monospace',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    letterSpacing: '2px',
                    wordBreak: 'break-all',
                    marginBottom: '1rem'
                  }}>
                    {formatPinForDisplay(transaction.metadata.electricityToken)}
                  </p>
                  <Button
                    onClick={handleCopyToken}
                    data-copy-token
                    variant="outline"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      minHeight: '44px',
                      borderRadius: '12px',
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Copy style={{ width: '16px', height: '16px' }} />
                    Copy Token
                  </Button>
                </div>
              </div>

              <div style={{
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '1rem',
                marginTop: '1rem'
              }}>              </div>
            </>
          )}

          {isGlobalServices && transaction.metadata?.pin && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8fafe',
                borderRadius: '12px',
                border: '2px dashed #2D8CCA'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your Global Services PIN
                  </p>
                  <p style={{
                    fontFamily: 'Monaco, Courier, monospace',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1f2937',
                    letterSpacing: '3px',
                    marginBottom: '1rem'
                  }}>
                    {transaction.metadata.pin}
                  </p>
                  <Button
                    onClick={handleCopyToken}
                    data-copy-token
                    variant="outline"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      minHeight: '44px',
                      borderRadius: '12px',
                      width: '100%'
                    }}
                  >
                    <Copy style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                    Copy PIN
                  </Button>
                </div>
              </div>
              <div style={{
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '1rem',
                marginTop: '1rem'
              }} />
            </>
          )}

          {/* Flash Cash-Out PIN (if available) */}
          {isCashOut && transaction.metadata?.pin && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '12px',
                border: '2px dashed #d1d5db'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your EeziCash PIN
                  </p>
                  <p style={{
                    fontFamily: 'Monaco, Courier, monospace',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1f2937',
                    letterSpacing: '3px',
                    marginBottom: '1rem'
                  }}>
                    {transaction.metadata.pin}
                  </p>
                  <Button
                    onClick={handleCopyToken}
                    data-copy-token
                    variant="outline"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      minHeight: '44px',
                      borderRadius: '12px',
                      width: '100%',
                      backgroundColor: '#ffffff',
                      border: '2px solid #2D8CCA',
                      color: '#2D8CCA'
                    }}
                  >
                    <Copy style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                    Copy PIN
                  </Button>
                </div>
              </div>

              {/* PIN Value and Fee Breakdown */}
              {transaction.metadata?.faceValue && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8fafe',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  marginTop: '1rem'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        PIN Value
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        R {Number(transaction.metadata.faceValue).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        Transaction Fee
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        R {(transaction.metadata.transactionFee || transaction.metadata.customerFee / 100 || 8).toFixed(2)}
                      </span>
                    </div>
                    <div style={{
                      paddingTop: '0.75rem',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1f2937'
                      }}>
                        Total Paid
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#dc2626'
                      }}>
                        R {Math.abs(transaction.amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* eeziAirtime / eeziPower Token PIN (if available) */}
          {isEeziToken && transaction.metadata?.pin && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: isEeziPowerToken ? '#fffbeb' : '#f0fdf4',
                borderRadius: '12px',
                border: `2px dashed ${isEeziPowerToken ? '#f59e0b' : '#86BE41'}`
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your {eeziTokenLabel} PIN
                  </p>
                  <p style={{
                    fontFamily: 'Monaco, Courier, monospace',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1f2937',
                    letterSpacing: '3px',
                    marginBottom: '0.5rem'
                  }}>
                    {formatPinForDisplay(transaction.metadata.pin)}
                  </p>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#4b5563',
                    marginBottom: (eeziPowerRedemptionGuide || eeziAirtimeRedemptionGuide) ? '0.75rem' : '1rem',
                    lineHeight: 1.4
                  }}>
                    {isEeziPowerToken
                      ? 'Keep this token private until you have loaded electricity on your meter.'
                      : eeziAirtimeRedemptionGuide
                        ? 'Use the USSD steps below to load airtime or data on the phone you want to top up.'
                        : 'Dial *130*3621*3*[PIN]# from the phone you want to top up.'}
                  </p>
                  <Button
                    onClick={handleCopyToken}
                    data-copy-token
                    variant="outline"
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      minHeight: '44px',
                      borderRadius: '12px',
                      width: '100%',
                      backgroundColor: '#ffffff',
                      border: `2px solid ${isEeziPowerToken ? '#f59e0b' : '#86BE41'}`,
                      color: isEeziPowerToken ? '#d97706' : '#16a34a'
                    }}
                  >
                    <Copy style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                    {isEeziAirtimeToken && eeziAirtimeRedemptionGuide ? 'Copy dial string' : 'Copy PIN'}
                  </Button>
                </div>
              </div>

              {eeziPowerRedemptionGuide && (
                <div style={{ marginTop: '1rem' }}>
                  <VoucherRedemptionInstructions guide={eeziPowerRedemptionGuide} />
                </div>
              )}

              {eeziAirtimeRedemptionGuide && transaction.metadata?.pin && (
                <div style={{ marginTop: '1rem' }}>
                  <VoucherRedemptionInstructions
                    guide={eeziAirtimeRedemptionGuide}
                    voucherPin={transaction.metadata.pin}
                  />
                </div>
              )}

              <div style={{
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '1rem',
                marginTop: '1rem'
              }} />
            </>
          )}

          {/* OTT Withdraw Cash credentials, masked until step-up authentication succeeds */}
          {hasCashoutCredential && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8fafe',
                borderRadius: '12px',
                border: '2px dashed #2D8CCA'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your Withdraw Cash Credentials
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    {cashoutCredentialParts.map((part: any, index: number) => {
                      const revealedValue = typeof part?.value === 'string' ? part.value : '';
                      return (
                        <div
                          key={`${part?.type || 'credential'}-${index}`}
                          style={{
                            padding: '0.75rem',
                            backgroundColor: '#ffffff',
                            borderRadius: '10px',
                            border: '1px solid #dbeafe'
                          }}
                        >
                          <p style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '12px',
                            color: '#6b7280',
                            marginBottom: '6px'
                          }}>
                            {part?.label || 'Credential'}
                          </p>
                          <p style={{
                            fontFamily: 'Monaco, Courier, monospace',
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#1f2937',
                            letterSpacing: '2px',
                            wordBreak: 'break-all',
                            marginBottom: revealedValue && showCashoutCredential ? '0.75rem' : 0
                          }}>
                            {showCashoutCredential && revealedValue
                              ? formatPinForDisplay(revealedValue)
                              : part?.maskedCode}
                          </p>
                          {showCashoutCredential && revealedValue && (
                            <Button
                              onClick={() => handleCopyCashoutCredential(revealedValue)}
                              variant="outline"
                              style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '13px',
                                fontWeight: '500',
                                minHeight: '40px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                border: '2px solid #2D8CCA',
                                color: '#2D8CCA'
                              }}
                            >
                              <Copy style={{ width: '15px', height: '15px', marginRight: '8px' }} />
                              {cashoutCopied ? 'Copied!' : `Copy ${part?.label || 'Credential'}`}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#4b5563',
                    marginBottom: '1rem',
                    lineHeight: 1.4
                  }}>
                    Keep this private. Use it only at the collection channel shown in the provider SMS.
                  </p>
                  {!showCashoutCredential && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <input
                        type="password"
                        value={credentialStepUp}
                        onChange={(event) => setCredentialStepUp(event.target.value)}
                        placeholder="Enter account password or wallet PIN"
                        aria-label="Account password or wallet PIN"
                        style={{
                          width: '100%',
                          minHeight: '44px',
                          borderRadius: '12px',
                          border: '1px solid #d1d5db',
                          padding: '0 0.9rem',
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px'
                        }}
                      />
                      {credentialRevealError && (
                        <p style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '12px',
                          color: '#dc2626',
                          margin: 0
                        }}>
                          {credentialRevealError}
                        </p>
                      )}
                      <Button
                        onClick={handleRevealCashoutCredentials}
                        variant="outline"
                        disabled={credentialRevealLoading || !credentialStepUp.trim()}
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px',
                          fontWeight: '500',
                          minHeight: '44px',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          border: '2px solid #2D8CCA',
                          color: '#2D8CCA',
                          opacity: credentialRevealLoading || !credentialStepUp.trim() ? 0.6 : 1
                        }}
                      >
                        {credentialRevealLoading ? 'Verifying...' : 'Reveal'}
                      </Button>
                    </div>
                  )}
                  {showCashoutCredential && (
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        onClick={() => {
                          setShowCashoutCredential(false);
                          setRevealedCashoutCredentials(null);
                        }}
                        variant="outline"
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px',
                          fontWeight: '500',
                          minHeight: '44px',
                          borderRadius: '12px'
                        }}
                      >
                        Hide
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '1rem',
                marginTop: '1rem'
              }} />
            </>
          )}

          {/* Retail voucher PIN, masked until step-up authentication succeeds */}
          {hasVoucherCredential && (
            <>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8fafe',
                borderRadius: '12px',
                border: '2px dashed #2D8CCA'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    Your voucher details
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    {voucherCredentialParts.map((part: any, index: number) => {
                      const partLabel = part?.label || 'Voucher PIN';
                      const revealedValue = showVoucherPin && typeof part?.value === 'string' ? part.value : '';
                      const maskedValue = part?.maskedCode || '****';
                      return (
                        <div
                          key={`${part?.type || 'part'}-${index}`}
                          style={{
                            padding: '0.75rem',
                            backgroundColor: '#ffffff',
                            borderRadius: '10px',
                            border: '1px solid #dbeafe'
                          }}
                        >
                          <p style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '12px',
                            color: '#6b7280',
                            marginBottom: '6px'
                          }}>
                            {partLabel}
                          </p>
                          <p style={{
                            fontFamily: 'Monaco, Courier, monospace',
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#1f2937',
                            letterSpacing: '2px',
                            wordBreak: 'break-all',
                            marginBottom: revealedValue ? '0.75rem' : 0
                          }}>
                            {revealedValue ? formatPinForDisplay(revealedValue) : maskedValue}
                          </p>
                          {revealedValue && (
                            <Button
                              onClick={() => handleCopyVoucherPin(revealedValue, partLabel)}
                              variant="outline"
                              style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '13px',
                                fontWeight: '500',
                                minHeight: '40px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                border: '2px solid #2D8CCA',
                                color: '#2D8CCA'
                              }}
                            >
                              <Copy style={{ width: '15px', height: '15px', marginRight: '8px' }} />
                              {voucherCopied && voucherCopiedLabel === partLabel ? 'Copied!' : `Copy ${partLabel}`}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {voucherSmsNotice && (
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#1e40af',
                      marginBottom: '1rem',
                      lineHeight: 1.4
                    }}>
                      {voucherSmsNotice}
                    </p>
                  )}
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    color: '#4b5563',
                    marginBottom: '1rem',
                    lineHeight: 1.4
                  }}>
                    Keep this private. Use it only as shown by the provider.
                  </p>
                  {!showVoucherPin && voucherRevealAvailable && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <input
                        type="password"
                        value={voucherStepUp}
                        onChange={(event) => setVoucherStepUp(event.target.value)}
                        placeholder="Enter account password or wallet PIN"
                        aria-label="Account password or wallet PIN"
                        style={{
                          width: '100%',
                          minHeight: '44px',
                          borderRadius: '12px',
                          border: '1px solid #d1d5db',
                          padding: '0 0.9rem',
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px'
                        }}
                      />
                      {voucherRevealError && (
                        <p style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '12px',
                          color: '#dc2626',
                          margin: 0
                        }}>
                          {voucherRevealError}
                        </p>
                      )}
                      <Button
                        onClick={handleRevealVoucherCredentials}
                        variant="outline"
                        disabled={voucherRevealLoading || !voucherStepUp.trim()}
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px',
                          fontWeight: '500',
                          minHeight: '44px',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          border: '2px solid #2D8CCA',
                          color: '#2D8CCA',
                          opacity: voucherRevealLoading || !voucherStepUp.trim() ? 0.6 : 1
                        }}
                      >
                        {voucherRevealLoading ? 'Verifying...' : 'Reveal'}
                      </Button>
                    </div>
                  )}
                  {!showVoucherPin && !voucherRevealAvailable && (
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#6b7280',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      The full voucher PIN for this purchase was not stored securely and cannot be retrieved in the app. Contact support with your transaction reference if you need help.
                    </p>
                  )}
                  {showVoucherPin && (
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        onClick={() => {
                          setShowVoucherPin(false);
                          setRevealedVoucher(null);
                        }}
                        variant="outline"
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px',
                          fontWeight: '500',
                          minHeight: '44px',
                          borderRadius: '12px'
                        }}
                      >
                        Hide
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {voucherRedemptionGuide && (
                <div style={{ marginTop: '1rem' }}>
                  <VoucherRedemptionInstructions guide={voucherRedemptionGuide} />
                </div>
              )}

              <div style={{
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '1rem',
                marginTop: '1rem'
              }} />
            </>
          )}

          {/* Transaction Details */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '1.25rem',
              paddingBottom: '0.75rem',
              borderBottom: '2px solid #e5e7eb'
            }}>
              Transaction Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Description */}
              {displayDescription && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Description
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937',
                    maxWidth: '250px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'right'
                  }}
                  title={displayDescription}
                  >
                    {displayDescription}
                  </span>
                </div>
              )}

              {/* Type */}
              {transaction.type && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Type
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {formatTransactionType(transaction.type)}
                  </span>
                </div>
              )}

              {/* Beneficiary Name (if electricity) */}
              {isElectricity && transaction.metadata?.beneficiaryName && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Meter
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {transaction.metadata.beneficiaryName}
                  </span>
                </div>
              )}

              {/* Meter Number (if electricity) */}
              {isElectricity && transaction.metadata?.beneficiaryMeter && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Meter Number
                  </span>
                  <span style={{
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {transaction.metadata.beneficiaryMeter}
                  </span>
                </div>
              )}

              {/* Meter Type (if electricity) */}
              {isElectricity && transaction.metadata?.meterType && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Meter Type
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {transaction.metadata.meterType}
                  </span>
                </div>
              )}

              {/* Amount */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6b7280'
                }}>
                  Amount
                </span>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: transaction.amount >= 0 ? '#16a34a' : '#dc2626'
                }}>
                  R {Math.abs(transaction.amount).toFixed(2)}
                </span>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6b7280'
                }}>
                  Status
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#f0fdf4',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #86BE41'
                }}>
                  <CheckCircle style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#16a34a',
                    textTransform: 'capitalize'
                  }}>
                    {transaction.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            style={{
              width: '100%',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: '600',
              padding: '12px',
              borderRadius: '12px',
              minHeight: '48px'
            }}
          >
            Done
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
